import React from 'react';
import {
  Group,
  ActionIcon,
  Tooltip,
  Box,
  Badge,
  Divider
} from '@mantine/core';
import {
  IconZoomIn,
  IconZoomOut,
  IconRefresh,
  IconGrid3x3,
  IconDownload,
  IconMaximize,
  IconMinimize,
  IconSettings,
  IconEdit,
  IconPalette
} from '@tabler/icons-react';

interface FloorPlanToolbarProps {
  zoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetView: () => void;
  onToggleGridSnap: () => void;
  onExportView: () => void;
  onToggleFullscreen: () => void;
  onToggleEditMode?: () => void;
  gridSnapEnabled: boolean;
  isFullscreen: boolean;
  isEditMode?: boolean;
  canEdit?: boolean;
  roomCount: number;
  assignmentCount: number;
  conflictCount: number;
  loading?: boolean;
}

/**
 * FloorPlanToolbar - Advanced toolbar for floor plan canvas controls
 * 
 * Features:
 * - Zoom controls with visual feedback
 * - Grid snap toggle with state indicator
 * - Export functionality for PNG/SVG
 * - Fullscreen toggle
 * - Real-time statistics display
 * - Responsive design for mobile/tablet
 */
const FloorPlanToolbar: React.FC<FloorPlanToolbarProps> = ({
  zoom,
  onZoomIn,
  onZoomOut,
  onResetView,
  onToggleGridSnap,
  onExportView,
  onToggleFullscreen,
  onToggleEditMode,
  gridSnapEnabled,
  isFullscreen,
  isEditMode = false,
  canEdit = false,
  roomCount,
  assignmentCount,
  conflictCount,
  loading = false
}) => {
  return (
    <Box
      p="sm"
      style={{
        backgroundColor: 'var(--mantine-color-gray-0)',
        borderBottom: '1px solid var(--mantine-color-gray-3)',
        borderRadius: 'var(--mantine-radius-md) var(--mantine-radius-md) 0 0'
      }}
    >
      <Group justify="space-between" align="center">
        {/* Left Controls */}
        <Group gap="xs">
          {/* Zoom Controls */}
          <Group gap="xs">
            <Tooltip label="Zoom Out" position="bottom">
              <ActionIcon
                variant="light"
                size="sm"
                onClick={onZoomOut}
                disabled={loading || zoom <= 0.1}
                color="blue"
              >
                <IconZoomOut size={16} />
              </ActionIcon>
            </Tooltip>
            
            <Badge
              variant="light"
              color="blue"
              size="sm"
              style={{ minWidth: '60px', textAlign: 'center' }}
            >
              {Math.round(zoom * 100)}%
            </Badge>
            
            <Tooltip label="Zoom In" position="bottom">
              <ActionIcon
                variant="light"
                size="sm"
                onClick={onZoomIn}
                disabled={loading || zoom >= 5}
                color="blue"
              >
                <IconZoomIn size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>

          <Divider orientation="vertical" size="sm" />

          {/* Edit Layout Mode Toggle (Coordinator/Admin only) */}
          {canEdit && onToggleEditMode && (
            <>
              <Tooltip 
                label={isEditMode ? "Exit Edit Mode" : "Edit Mode - Position rooms, resize, and create new ones"} 
                position="bottom"
              >
                <ActionIcon
                  variant={isEditMode ? "filled" : "light"}
                  size="sm"
                  onClick={onToggleEditMode}
                  disabled={loading}
                  color={isEditMode ? "blue" : "indigo"}
                >
                  <IconEdit size={16} />
                </ActionIcon>
              </Tooltip>
              
              <Divider orientation="vertical" size="sm" />
            </>
          )}


          {/* View Controls */}
          <Group gap="xs">
            <Tooltip label="Reset View" position="bottom">
              <ActionIcon
                variant="light"
                size="sm"
                onClick={onResetView}
                disabled={loading}
                color="green"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>

            <Tooltip 
              label={gridSnapEnabled ? "Disable Grid Snap" : "Enable Grid Snap"} 
              position="bottom"
            >
              <ActionIcon
                variant={gridSnapEnabled ? "filled" : "light"}
                size="sm"
                onClick={onToggleGridSnap}
                disabled={loading}
                color="orange"
              >
                <IconGrid3x3 size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>

        {/* Center Stats */}
        <Group gap="md">
          <Badge
            variant="light"
            color="blue"
            size="sm"
            leftSection={<IconGrid3x3 size={12} />}
          >
            {roomCount} Rooms
          </Badge>
          
          <Badge
            variant="light"
            color="green"
            size="sm"
          >
            {assignmentCount} Assigned
          </Badge>
          
          {conflictCount > 0 && (
            <Badge
              variant="light"
              color="red"
              size="sm"
            >
              {conflictCount} Conflicts
            </Badge>
          )}
        </Group>

        {/* Right Controls */}
        <Group gap="xs">
          <Tooltip label="Export View" position="bottom">
            <ActionIcon
              variant="light"
              size="sm"
              onClick={onExportView}
              disabled={loading}
              color="purple"
            >
              <IconDownload size={16} />
            </ActionIcon>
          </Tooltip>

          <Tooltip 
            label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"} 
            position="bottom"
          >
            <ActionIcon
              variant="light"
              size="sm"
              onClick={onToggleFullscreen}
              color="gray"
            >
              {isFullscreen ? <IconMinimize size={16} /> : <IconMaximize size={16} />}
            </ActionIcon>
          </Tooltip>

          <Tooltip label="Settings" position="bottom">
            <ActionIcon
              variant="light"
              size="sm"
              color="gray"
            >
              <IconSettings size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Box>
  );
};

export default FloorPlanToolbar;
